import { Link } from 'react-router-dom';
import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BenefitClaim, EligibilityTrace, RegistryProfile } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

/** S02 now exposes GET /api/v1/benefits/claims/me — real server-side history,
 * not a per-device cache (previously the only option here). */
export function MyBenefits() {
  const { persona } = useAuth();
  const [traces, setTraces] = useState<Record<string, EligibilityTrace>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const profileQ = useApiQuery<RegistryProfile>(persona?.nin ? `/api/v1/registry/profile/${persona.nin}` : null);
  const claimsQ = useApiQuery<{ items: BenefitClaim[] }>('/api/v1/benefits/claims/me');
  const claims = claimsQ.data?.items ?? [];

  const checkTrace = async (claim: BenefitClaim) => {
    setBusyId(claim.id);
    setError(null);
    try {
      const trace = await api.get<EligibilityTrace>(`/api/v1/benefits/claims/${claim.id}/eligibility-trace`);
      setTraces((t) => ({ ...t, [claim.id]: trace }));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not fetch the eligibility trace');
    } finally {
      setBusyId(null);
    }
  };

  const beneficiary = profileQ.data?.beneficiary;
  const activeFlags = profileQ.data?.vitalFlags.filter((f) => !f.clearedAt) ?? [];

  return (
    <div>
      <PageHeader
        title="My Benefits"
        subtitle="Statutory benefit claims you've submitted through this portal"
        actions={
          <Link to="/citizen/apply" className="btn-primary">
            + Apply for a benefit
          </Link>
        }
      />

      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {activeFlags.length > 0 ? (
        <div className="mb-4">
          <ErrorNote
            message={`Your registry record has an active flag (${activeFlags.map((f) => f.type).join(', ')}). This can pause benefit payments — contact your district ASP office if this looks wrong.`}
          />
        </div>
      ) : null}

      {profileQ.loading ? (
        <Spinner label="Loading your registry profile…" />
      ) : beneficiary ? (
        <div className="card mb-6 px-4 py-3.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Registered as</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{beneficiary.fullName}</div>
          <div className="text-xs text-slate-500">
            NIN {beneficiary.nin} · District {beneficiary.districtId.replaceAll('-', ' ')} · Paid by{' '}
            {beneficiary.payMethod === 'BANK' ? `bank transfer (${beneficiary.bankAccount ?? 'no account on file'})` : 'district cash office'}
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <EmptyState
            title="You are not yet in the social registry"
            hint="Visit your nearest district ASP office to register before applying for a benefit."
          />
        </div>
      )}

      <div className="card overflow-x-auto">
        {claimsQ.loading ? (
          <Spinner label="Loading your claims…" />
        ) : claimsQ.error ? (
          <ErrorNote message={claimsQ.error} />
        ) : claims.length === 0 ? (
          <EmptyState title="No claims yet" hint="Use Apply to submit your first statutory benefit claim." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Reference</th>
                <th className="table-head">Benefit</th>
                <th className="table-head">Submitted</th>
                <th className="table-head">Status (as last known)</th>
                <th className="table-head">Eligibility checks</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => {
                const trace = traces[c.id];
                return (
                  <tr key={c.id}>
                    <td className="table-cell font-medium">{c.refNumber}</td>
                    <td className="table-cell">{c.benefitType?.name ?? c.benefitTypeId}</td>
                    <td className="table-cell text-slate-500">{formatDate(c.createdAt)}</td>
                    <td className="table-cell">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="table-cell">
                      {trace ? (
                        <ul className="space-y-0.5 text-xs">
                          {trace.trace.map((t, i) => (
                            <li key={i} className={t.pass ? 'text-seagreen-600' : 'text-rose-600'}>
                              {t.pass ? '✓' : '✗'} {t.rule} — {t.detail}
                            </li>
                          ))}
                          {trace.trace.length === 0 ? <li className="text-slate-400">No conditions to check</li> : null}
                        </ul>
                      ) : (
                        <button
                          className="btn-secondary !px-2.5 !py-1 text-xs"
                          disabled={busyId === c.id}
                          onClick={() => checkTrace(c)}
                        >
                          {busyId === c.id ? 'Checking…' : 'Check status'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-400">
        Submit an appeal from the Appeals tab if you disagree with a decision.
      </p>
    </div>
  );
}
